/**
 * MSAL singleton — lazily initialized from the backend /api/auth/config.
 *
 * Why lazy?
 *   • Tenant ID / Client ID live in backend .env, not the frontend bundle,
 *     so the SPA cannot hard-code them at build time.
 *   • We only construct the MSAL PublicClientApplication once we have those
 *     values from /api/auth/config — then cache it for the rest of the session.
 *
 * Flow: REDIRECT (not popup).
 *   loginPopup hits Cross-Origin-Opener-Policy errors on modern Chrome/Edge
 *   over HTTPS — the popup can't postMessage back.  Redirect flow has no popup,
 *   no COOP issues, and is what Microsoft recommends for SPAs in 2025+.
 *
 *   • startSsoLogin()   → kicks off Entra ID redirect (page navigates away)
 *   • handleRedirectResult() → call on every app mount; returns the id_token
 *     if we just got back from Entra, else null.
 */

import { PublicClientApplication } from '@azure/msal-browser';
import { getAuthConfig } from '../api/client';

let _msalPromise = null;
let _msalConfig = null;

const SCOPES = ['openid', 'profile', 'email', 'User.Read'];

async function _init() {
  const { data } = await getAuthConfig();
  _msalConfig = data;
  if (!data.sso_enabled) return null;

  const pca = new PublicClientApplication({
    auth: {
      clientId: data.sso.client_id,
      authority: `https://login.microsoftonline.com/${data.sso.tenant_id}`,
      // IT registered:
      //   https://localhost:3000/auth/callback   (dev — note HTTPS)
      //   https://dash.deliverhealth.com/auth/callback   (prod)
      redirectUri: process.env.REACT_APP_MSAL_REDIRECT_URI || `${window.location.origin}/auth/callback`,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  });
  await pca.initialize();
  return pca;
}

export function getMsal() {
  if (!_msalPromise) _msalPromise = _init();
  return _msalPromise;
}

export async function getAuthConfigCached() {
  if (_msalConfig) return _msalConfig;
  const { data } = await getAuthConfig();
  _msalConfig = data;
  return data;
}

/**
 * Kick off a full-page redirect to Entra ID.  The current page navigates away;
 * after sign-in (and MFA) Entra redirects back to `redirectUri` with the token
 * in the URL fragment.  handleRedirectResult() picks it up on the next page load.
 *
 * NOTE: This function does NOT return — the page navigates away before the
 * promise can resolve.  Callers shouldn't await meaningful work after it.
 */
export async function startSsoLogin() {
  const pca = await getMsal();
  if (!pca) throw new Error('SSO is not configured on the backend.');
  // Stash where the user wanted to go so we can navigate back after callback.
  try { sessionStorage.setItem('dash-sso-return-to', window.location.pathname + window.location.search); } catch {}
  await pca.loginRedirect({
    scopes: SCOPES,
    prompt: 'select_account',
  });
}

/**
 * Call once on app mount.  Returns:
 *   { id_token, account, returnTo }  if we just landed back from Entra ID,
 *   null  otherwise.
 *
 * Safe to call on every page — MSAL no-ops when no redirect is pending.
 */
export async function handleRedirectResult() {
  const pca = await getMsal();
  if (!pca) return null;
  const result = await pca.handleRedirectPromise();
  if (!result) return null;
  let returnTo = '/admin';
  try {
    const stashed = sessionStorage.getItem('dash-sso-return-to');
    if (stashed) returnTo = stashed;
    sessionStorage.removeItem('dash-sso-return-to');
  } catch {}
  return {
    id_token: result.idToken,
    account: result.account,
    returnTo,
  };
}

export async function ssoLogout() {
  const pca = await getMsal();
  if (!pca) return;
  const account = pca.getAllAccounts()[0];
  if (account) {
    // Best-effort silent logout — never block on Entra's logout endpoint.
    try { await pca.logoutRedirect({ account, onRedirectNavigate: () => false }); } catch {}
  }
}
